import { ItemListType } from '../types/common';

/**
 * 項目リストのIDを正規化する
 * 既存のIDは保持し、新規項目（id: '0'または未設定）のみに新しいIDを採番する
 *
 * 採番は「現在のリスト」と「過去に使用されたID」の両方の最大値+1から行う（#3242）。
 * 削除済みのIDを usedItemIds で渡すことで、削除した項目のIDが新規項目に再利用され、
 * マスタ照合・辞書設定・出力設定の参照が別の項目を指してしまう問題を防ぐ。
 *
 * @param itemList - 正規化する項目リスト
 * @param usedItemIds - 過去に使用されたIDの集合（pattern_history から収集する）。
 *                      未指定の場合は現在のリストのみから採番する（従来動作）
 * @returns 正規化された項目リスト
 *
 * @example
 * // 親項目: "1", "2", "3", ...（既存IDは保持）
 * // 子項目: "1-1", "1-2", "2-1", "2-2", ...（既存IDは保持）
 */
export const normalizeItemListIds = (
  itemList: ItemListType[],
  usedItemIds?: ReadonlySet<string>,
): ItemListType[] => {
  // 親項目IDの最大値を求める。現在のリストに加え、過去に使用されたID（削除済みを含む）も
  // 対象にすることで、削除によって最大値が下がり同じ番号が再利用されるのを防ぐ
  let maxParentId = 0;
  const applyParentId = (id: string | undefined): void => {
    if (!id || id === '0') return;
    // ハイフンを含まない数値IDを取得
    const baseId = id.split('-')[0];
    const idNum = parseInt(baseId, 10);
    if (!isNaN(idNum) && idNum > maxParentId) {
      maxParentId = idNum;
    }
  };

  itemList.forEach(item => applyParentId(item.id));
  usedItemIds?.forEach(id => applyParentId(id));

  // 親IDごとの子項目IDの最大値を、過去に使用されたIDから求めておく。
  // 子は「親ID-連番」形式のため、親IDごとに分けて最大値を管理する
  const usedMaxChildIdByParent = new Map<string, number>();
  usedItemIds?.forEach(id => {
    if (!id || id === '0') return;
    const parts = id.split('-');
    if (parts.length !== 2) return;
    const childNum = parseInt(parts[1], 10);
    if (isNaN(childNum)) return;
    const current = usedMaxChildIdByParent.get(parts[0]) ?? 0;
    if (childNum > current) {
      usedMaxChildIdByParent.set(parts[0], childNum);
    }
  });

  return itemList.map(item => {
    // 既存のIDがある場合（'0'以外）は保持
    let parentId = item.id;
    if (!parentId || parentId === '0') {
      // 新規項目の場合のみ、新しいIDを採番
      maxParentId++;
      parentId = String(maxParentId);
    }

    // 子項目のIDを処理。過去に使用された子IDの最大値を初期値にする（#3242）
    let maxChildId = usedMaxChildIdByParent.get(parentId) ?? 0;
    (item.children || []).forEach(child => {
      if (child.id && child.id !== '0') {
        // 既存の子項目IDから最大値を取得（例: "1-3" -> 3）
        const parts = child.id.split('-');
        if (parts.length === 2) {
          const childNum = parseInt(parts[1], 10);
          if (!isNaN(childNum) && childNum > maxChildId) {
            maxChildId = childNum;
          }
        }
      }
    });

    return {
      ...item,
      id: parentId,
      is_active: item.is_active ?? true, // is_activeを必ず持つ（デフォルト: true）
      is_ocr_target: item.is_ocr_target ?? true, // OCR対象フラグを必ず持つ（デフォルト: true。#2968）
      children: (item.children || []).map(child => {
        // 既存のIDがある場合（'0'以外）は保持
        let childId = child.id;
        if (!childId || childId === '0') {
          // 新規子項目の場合のみ、新しいIDを採番
          maxChildId++;
          childId = `${parentId}-${maxChildId}`;
        }
        return {
          ...child,
          id: childId,
          is_active: child.is_active ?? true, // is_activeを必ず持つ（デフォルト: true）
          is_ocr_target: child.is_ocr_target ?? true, // OCR対象フラグを必ず持つ（デフォルト: true。#2968）
        };
      }),
    };
  });
};

/**
 * 項目リストから全ID（親・子）を収集する（#3242）
 * 採番済みでないID（'0'・未設定）は対象外
 */
export const collectItemListIds = (itemList: ItemListType[]): Set<string> => {
  const ids = new Set<string>();
  itemList.forEach(item => {
    if (item.id && item.id !== '0') ids.add(item.id);
    (item.children || []).forEach(child => {
      if (child.id && child.id !== '0') ids.add(child.id);
    });
  });
  return ids;
};
